import { api } from "./client";
import type {
  Order,
  OrderStatus,
  OrdersResponse,
  Pagination,
  PaymentStatus,
  Plan,
  StockStatus,
} from "./types";

// ---------- Admin: Orders ----------

export interface AdminOrder extends Order {
  tenant: { id: number; name: string; plan: Plan } | null;
}

export interface AdminOrdersResponse {
  orders: AdminOrder[];
  pagination: Pagination;
}

export interface AdminOrderQuery {
  status?: OrderStatus | "";
  tenant_id?: number;
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
}

export async function fetchAdminOrders(params: AdminOrderQuery = {}): Promise<AdminOrdersResponse> {
  const cleaned: Record<string, unknown> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== "" && v !== null) cleaned[k] = v;
  });
  const resp = await api.get<AdminOrdersResponse>("/admin/orders", { params: cleaned });
  return resp.data;
}

export async function fetchAdminOrder(id: number): Promise<AdminOrder> {
  const resp = await api.get<{ order: AdminOrder }>(`/admin/orders/${id}`);
  return resp.data.order;
}

export async function approveOrder(id: number): Promise<{ order: AdminOrder; export_csv_path: string }> {
  const resp = await api.put<{ order: AdminOrder; export_csv_path: string }>(
    `/admin/orders/${id}/approve`,
  );
  return resp.data;
}

export async function cancelOrder(id: number): Promise<AdminOrder> {
  const resp = await api.put<{ order: AdminOrder }>(`/admin/orders/${id}/cancel`);
  return resp.data.order;
}

export async function shipOrder(
  id: number,
  body: { tracking_carrier: string; tracking_number: string },
): Promise<AdminOrder> {
  const resp = await api.put<{ order: AdminOrder }>(
    `/admin/orders/${id}/ship`, body,
  );
  return resp.data.order;
}

export interface BulkShipResult {
  shipped: AdminOrder[];
  skipped: { order_id: number; reason: string }[];
}

export async function bulkShipOrders(body: {
  order_ids: number[];
  tracking_carrier: string;
  tracking_number: string;
}): Promise<BulkShipResult> {
  const resp = await api.post<BulkShipResult>("/admin/orders/bulk-ship", body);
  return resp.data;
}

export async function updateTracking(
  id: number,
  body: {
    tracking_carrier: string;
    tracking_number: string;
    resend_email?: boolean;
  },
): Promise<AdminOrder> {
  const resp = await api.put<{ order: AdminOrder }>(
    `/admin/orders/${id}/tracking`, body,
  );
  return resp.data.order;
}

export interface TrackingCarrier {
  value: string;
  label: string;
  has_tracking_url: boolean;
}

export async function fetchTrackingCarriers(): Promise<TrackingCarrier[]> {
  const resp = await api.get<{ carriers: TrackingCarrier[] }>(
    "/admin/orders/tracking-carriers",
  );
  return resp.data.carriers;
}

export async function completeOrder(id: number): Promise<AdminOrder> {
  const resp = await api.put<{ order: AdminOrder }>(`/admin/orders/${id}/complete`);
  return resp.data.order;
}

/**
 * 出庫指示 CSV を一括ダウンロードする（Phase 10B / Phase 3）。
 * 既定では status=approved の未出荷発注を対象とする。
 */
export async function downloadShippingCsv(params?: {
  status?: OrderStatus;
  orderIds?: number[];
}): Promise<{ blob: Blob; filename: string }> {
  const query: Record<string, string> = {};
  if (params?.orderIds && params.orderIds.length > 0) {
    query.order_ids = params.orderIds.join(",");
  } else if (params?.status) {
    query.status = params.status;
  }
  const resp = await api.get("/admin/orders/shipping-csv", {
    params: query,
    responseType: "blob",
  });
  // Content-Disposition からファイル名抽出
  const disp = (resp.headers?.["content-disposition"] as string | undefined) ?? "";
  const match = /filename="?([^"]+)"?/.exec(disp);
  const filename =
    match?.[1] ?? `shipping_${new Date().toISOString().replace(/[:.]/g, "-")}.csv`;
  return { blob: resp.data as Blob, filename };
}

// プロキシで発注履歴一覧 API を共通化したい場合のエイリアス（未使用）
export type { OrdersResponse };

// ---------- Admin: Tenants ----------

export interface AdminTenant {
  id: number;
  name: string;
  contact_name: string | null;
  user_emails: string[];
  plan: Plan;
  is_active: boolean;
  payment_status: PaymentStatus;
  past_due_since: string | null;
  cancellation_requested_at: string | null;
  cancellation_reason: string | null;
  cancellation_finalized_at: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  shipping_postal_code: string | null;
  shipping_address: string | null;
  store_code: string | null;
  recipient_name: string | null;
  recipient_phone: string | null;
  currency: string | null;
  created_at: string | null;
  updated_at: string | null;
  user_count: number;
}

export interface AdminTenantsResponse {
  tenants: AdminTenant[];
  pagination: Pagination;
}

export type TenantListFilter = "requested" | "finalized" | "";

export async function fetchTenants(
  page = 1,
  per_page = 20,
  cancellation: TenantListFilter = "",
): Promise<AdminTenantsResponse> {
  const params: Record<string, unknown> = { page, per_page };
  if (cancellation) params.cancellation = cancellation;
  const resp = await api.get<AdminTenantsResponse>("/admin/tenants", { params });
  return resp.data;
}

export interface CreateTenantParams {
  name: string;
  plan: Plan;
  shipping_postal_code?: string;
  shipping_address?: string;
  initial_email?: string;
  initial_password?: string;
}

export async function createTenant(params: CreateTenantParams): Promise<AdminTenant> {
  const resp = await api.post<{ tenant: AdminTenant }>("/admin/tenants", params);
  return resp.data.tenant;
}

export type UpdateTenantParams = Partial<
  Pick<
    AdminTenant,
    | "name"
    | "plan"
    | "is_active"
    | "shipping_postal_code"
    | "shipping_address"
    | "store_code"
    | "recipient_name"
    | "recipient_phone"
    | "currency"
  >
>;

export async function updateTenant(id: number, params: UpdateTenantParams): Promise<AdminTenant> {
  const resp = await api.put<{ tenant: AdminTenant }>(`/admin/tenants/${id}`, params);
  return resp.data.tenant;
}

export async function updateTenantPaymentStatus(
  id: number,
  payment_status: PaymentStatus,
): Promise<AdminTenant> {
  const resp = await api.put<{ tenant: AdminTenant }>(
    `/admin/tenants/${id}/payment-status`,
    { payment_status },
  );
  return resp.data.tenant;
}

export async function finalizeTenantCancellation(id: number): Promise<AdminTenant> {
  const resp = await api.put<{ tenant: AdminTenant }>(
    `/admin/tenants/${id}/finalize-cancellation`,
  );
  return resp.data.tenant;
}

export async function withdrawTenantCancellation(id: number): Promise<AdminTenant> {
  const resp = await api.delete<{ tenant: AdminTenant }>(
    `/admin/tenants/${id}/cancellation-request`,
  );
  return resp.data.tenant;
}

// ---------- Admin: Inventory ----------

export interface InventoryRow {
  product_id: number;
  jan: string;
  product_name: string;
  airlogi_stock: number;
  allocated_stock: number;
  public_stock: number;
  is_active: boolean;
}

export interface SyncLog {
  id: number;
  sync_type: "manual" | "scheduled" | "csv_import";
  status: "running" | "success" | "failed";
  products_updated: number;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface AirLogiImportResult {
  updated: number;
  skipped: number;
  skipped_jans: string[];
  sync_log: SyncLog;
}

export async function importAirLogiInventoryCsv(file: File): Promise<AirLogiImportResult> {
  const form = new FormData();
  form.append("file", file);
  const resp = await api.post<AirLogiImportResult>(
    "/admin/inventory/airlogi-import",
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return resp.data;
}

/**
 * airlogi-bridge から取得した在庫情報の rows を backend に POST する。
 * 戻り値の構造は手動 CSV 取込と同じ。
 */
export async function syncAirLogiInventoryFromBridge(
  rows: Array<Record<string, string | number | undefined>>,
): Promise<AirLogiImportResult> {
  const resp = await api.post<AirLogiImportResult>(
    "/admin/inventory/airlogi-sync",
    { rows },
  );
  return resp.data;
}

/**
 * backend が保持している airlogi-bridge の Chrome 拡張 ID を取得する。
 * 未設定 (.env 未記入) の場合は extension_id=null / configured=false を返す。
 */
export interface AirLogiExtensionInfo {
  extension_id: string | null;
  configured: boolean;
}

export async function fetchAirLogiExtensionId(): Promise<AirLogiExtensionInfo> {
  const resp = await api.get<AirLogiExtensionInfo>("/admin/airlogi/extension-id");
  return resp.data;
}

export interface InventoryResponse {
  inventory: InventoryRow[];
  pagination: Pagination;
  coefficient: number;
  safety_threshold: number;
  last_sync: SyncLog | null;
}

export async function fetchInventory(page = 1, per_page = 50): Promise<InventoryResponse> {
  const resp = await api.get<InventoryResponse>("/admin/inventory", {
    params: { page, per_page },
  });
  return resp.data;
}

export async function triggerSync(): Promise<SyncLog> {
  const resp = await api.post<{ sync_log: SyncLog }>("/admin/inventory/sync");
  return resp.data.sync_log;
}

export interface SyncLogsResponse {
  sync_logs: SyncLog[];
  pagination: Pagination;
}

export async function fetchSyncLogs(page = 1, per_page = 20): Promise<SyncLogsResponse> {
  const resp = await api.get<SyncLogsResponse>("/admin/inventory/sync-logs", {
    params: { page, per_page },
  });
  return resp.data;
}

export async function getCoefficient(): Promise<number> {
  const resp = await api.get<{ coefficient: number }>("/admin/inventory/coefficient");
  return resp.data.coefficient;
}

export async function setCoefficient(coefficient: number): Promise<{
  coefficient: number;
  products_recalculated: number;
}> {
  const resp = await api.put<{ coefficient: number; products_recalculated: number }>(
    "/admin/inventory/coefficient",
    { coefficient },
  );
  return resp.data;
}

export async function getSafetyThreshold(): Promise<number> {
  const resp = await api.get<{ safety_threshold: number }>(
    "/admin/inventory/safety-threshold",
  );
  return resp.data.safety_threshold;
}

export async function setSafetyThreshold(threshold: number): Promise<{
  safety_threshold: number;
  products_recalculated: number;
}> {
  const resp = await api.put<{
    safety_threshold: number;
    products_recalculated: number;
  }>("/admin/inventory/safety-threshold", { safety_threshold: threshold });
  return resp.data;
}

// ---------- Admin: Products (CSV import, list, edit) ----------

export interface AdminProduct {
  id: number;
  jan: string;
  product_name: string;
  unit_price: number;
  order_unit: number;
  tier: number;
  category: string | null;
  brand: string | null;
  image_url: string | null;
  notes: string | null;
  custom_field_1: string | null;
  custom_field_2: string | null;
  custom_field_3: string | null;
  airlogi_stock: number;
  public_stock: number;
  allocated_stock: number;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface CsvImportError {
  row: number;
  jan: string | null;
  reason: string;
}

export interface CsvImportResult {
  imported: number;
  updated: number;
  skipped: number;
  errors: CsvImportError[];
}

export async function importProductsCsv(file: File): Promise<CsvImportResult> {
  const form = new FormData();
  form.append("file", file);
  const resp = await api.post<CsvImportResult>("/admin/products/import", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return resp.data;
}

export async function updateAdminProduct(
  id: number,
  params: Partial<Pick<AdminProduct, "product_name" | "unit_price" | "order_unit" | "tier" | "category" | "brand" | "image_url" | "notes" | "is_active">>,
): Promise<AdminProduct> {
  const resp = await api.put<{ product: AdminProduct }>(`/admin/products/${id}`, params);
  return resp.data.product;
}

// ---------- Admin: Field Settings ----------

export type FieldKey =
  | "category"
  | "brand"
  | "notes"
  | "custom_field_1"
  | "custom_field_2"
  | "custom_field_3";

export interface FieldSetting {
  field_key: FieldKey;
  display_label: string;
  is_visible: boolean;
  display_order: number;
}

export async function fetchFieldSettings(): Promise<FieldSetting[]> {
  const resp = await api.get<{ fields: FieldSetting[] }>("/admin/settings/fields");
  return resp.data.fields;
}

export async function updateFieldSettings(fields: FieldSetting[]): Promise<FieldSetting[]> {
  const resp = await api.put<{ fields: FieldSetting[] }>("/admin/settings/fields", {
    fields,
  });
  return resp.data.fields;
}

// ---------- Admin: Applications ----------

export type ApplicationStatus = "pending" | "approved" | "rejected";

export interface AdminApplication {
  id: number;
  company_name: string;
  contact_name: string;
  email: string;
  phone: string | null;
  desired_plan: Plan;
  business_description: string | null;
  shipping_postal_code: string | null;
  shipping_address: string | null;
  status: ApplicationStatus;
  reviewed_at: string | null;
  review_note: string | null;
  approved_tenant_id: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AdminApplicationsResponse {
  applications: AdminApplication[];
  pagination: Pagination;
}

export async function fetchApplications(
  status?: ApplicationStatus | "",
  page = 1,
  per_page = 20,
): Promise<AdminApplicationsResponse> {
  const params: Record<string, unknown> = { page, per_page };
  if (status) params.status = status;
  const resp = await api.get<AdminApplicationsResponse>("/admin/applications", {
    params,
  });
  return resp.data;
}

export async function fetchApplication(id: number): Promise<AdminApplication> {
  const resp = await api.get<{ application: AdminApplication }>(
    `/admin/applications/${id}`,
  );
  return resp.data.application;
}

export interface ApproveResult {
  application: AdminApplication;
  tenant: { id: number; name: string; plan: Plan };
  initial_user: { email: string };
}

export async function approveApplication(
  id: number,
  params?: { note?: string },
): Promise<ApproveResult> {
  const resp = await api.put<ApproveResult>(
    `/admin/applications/${id}/approve`,
    params ?? {},
  );
  return resp.data;
}

export async function rejectApplication(
  id: number,
  params?: { note?: string; send_email?: boolean },
): Promise<AdminApplication> {
  const resp = await api.put<{ application: AdminApplication }>(
    `/admin/applications/${id}/reject`,
    params ?? {},
  );
  return resp.data.application;
}

// ---------- utility for stock status (共通) ----------

export function stockStatusFromPublic(publicStock: number): StockStatus {
  if (publicStock <= 0) return "out_of_stock";
  if (publicStock <= 10) return "low_stock";
  return "in_stock";
}
